import React from "react";
import Card from "components/card";
import TimePicker from "react-time-picker";
import Button from "components/button/Button";
import InputField from "components/fields/InputField";
import SwitchField from "components/fields/SwitchField";
import SelectField from "components/fields/SelectField";
import NumFormatField from "components/fields/NumFormatField";
import { AxiosResponse } from "axios";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { addDiscount, getDiscount, updateDiscount } from "api/DiscountAPI";
import { DiscountType } from "constants/discount";
import { StringParam, useQueryParams } from "use-query-params";
import { getGoods } from "api/GoodsAPI";
import { IGoods } from "../goods/types/goods.interface";
import { numericFormatter } from "react-number-format";
import { FaBoxes } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

const discountTypes: {
  value: string;
  label: string;
}[] = [
  {
    value: DiscountType.NOMINAL,
    label: "Nominal",
  },
  {
    value: DiscountType.PERCENTAGE,
    label: "Persentase",
  },
];

const DiscountForm: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isGoodsLoading, setIsGoodsLoading] = React.useState<boolean>(false);
  const [defaultTime] = React.useState<string>("00:00");
  const [goods, setGoods] = React.useState<IGoods[]>([]);
  const [selectedGoods, setSelectedGoods] = React.useState<IGoods[]>([]);
  const [res, setRes] = React.useState<any>();
  const [searchKey, setSearchKey] = React.useState<string>(null);
  const [query, setQuery] = useQueryParams({
    search: StringParam,
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({});

  const watchDiscountType = useWatch({
    control,
    name: "type",
  });

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (selectedGoods.length === 0) {
      toast.error("Minimal 1 barang harus dipilih!");
      return;
    }

    setIsLoading(true);

    data.discount =
      typeof data.discount === "string"
        ? data.discount.replace(/[%Rp.]/g, "")
        : data.discount;

    const start_at = new Date(
      data.start_at + " " + data.timestart_at
    ).toISOString();
    const end_at = new Date(data.end_at + " " + data.timeend_at).toISOString();

    const payload = {
      product_ids: selectedGoods.map((item) => item.id),
      is_active: data.is_active,
      discount: parseInt(data.discount),
      type: data.type.value,
      start_at: new Date(start_at),
      end_at: new Date(end_at),
    };

    // {
    //   is_active: boolean;
    //   name: string;
    //   type: {
    //     value: string;
    //     label: string;
    //   };
    //   discount: string;
    //   start_at: Date;
    //   timestart_at: string;
    //   end_at: Date;
    //   timeend_at: string;
    // }

    let promise = null;
    if (!slug) {
      promise = addDiscount(payload);
    } else {
      promise = updateDiscount(slug, payload);
    }

    toast.promise(promise, {
      loading: "Menyimpan promo...",
      success: (res: AxiosResponse) => {
        navigate("/admin/discounts");
        setIsLoading(false);
        return "Berhasil menyimpan promo!";
      },
      error: (err: any) => {
        console.log(payload);
        console.log(err);
        setIsLoading(false);
        return err?.response?.data?.message || "Gagal menyimpan promo!";
      },
    });
  };

  const getDetailData = (slug: string): void => {
    const promise = getDiscount(slug);

    toast.promise(promise, {
      loading: "Mengambil data promo...",
      success: (res) => {
        if (res.status === 200) {
          delete res.data.data.created_at;
          delete res.data.data.updated_at;
          delete res.data.data.slug;

          setRes(res);
          setSearchKey(res.data.data.name);
          getGoodsWithParams(res.data.data.name);

          reset({
            ...res.data.data,
            type: discountTypes.find(
              (item) => item.value === res.data.data.discount.type
            ),
            discount: res.data.data.discount.discount.toString(),
            start_at: res.data.data.discount.start_at.split("T")[0],
            end_at: res.data.data.discount.end_at.split("T")[0],
            timestart_at: new Date(res.data.data.discount.start_at)
              .toString()
              .split(" ")[4],
            timeend_at: new Date(res.data.data.discount.end_at)
              .toString()
              .split(" ")[4],
          });

          return res.data.message || "Berhasil mengambil data promo!";
        }
      },
      error: (err) => {
        return err?.response?.data?.message || "Gagal mengambil data promo!";
      },
    });
  };

  const getGoodsWithParams = (keyword?: string): void => {
    const promise = getGoods(null, null, keyword, null);

    promise
      .then((res) => {
        if (res.status === 200) {
          setGoods(res.data.data.items);
          setIsLoading(false);
          setIsGoodsLoading(false);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal memuat barang!");
      });
  };

  const handleSearchKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(e.target.value === "" ? null : e.target.value);
    getGoodsWithParams(e.target.value);
    setQuery({
      search: e.target.value === "" ? null : e.target.value,
    });
  };

  const handleSelectProduct = (item: IGoods) => {
    setSelectedGoods((prev) => {
      const isExist = prev.find((i) => i.id === item.id);
      if (isExist) {
        return prev.filter((i) => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const debounceRes = React.useMemo(() => {
    return debounce(handleSearchKey, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    getGoodsWithParams();

    if (query.search) {
      setSearchKey(query.search);
      getGoodsWithParams(query.search);
    }
    if (slug) {
      setIsLoading(true);
      setIsGoodsLoading(true);
      getDetailData(slug);
    }

    return () => {
      debounceRes.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (slug) {
      // setSelectedGoods(goods.find((item) => item.id === res.data.data.id));
      console.log(res);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goods]);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3 mt-3"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Tambah Promo
        </div>
      </header>
      <section className="mt-7">
        <div className="flex flex-col gap-5">
          <p className="ml-3 w-full whitespace-nowrap font-medium text-gray-600">
            Pilih Barang
          </p>
          <div className="flex h-12 w-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Cari barang..."
              onChange={(e) => debounceRes(e)}
              defaultValue={searchKey || query.search}
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>
        </div>
        <section>
          {isGoodsLoading ? (
            <div className="mt-5 flex h-28 items-center justify-center">
              <Spinner size="lg" className="text-brand-400" />
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3">
              {goods.length > 0 &&
                goods.map((item) => (
                  <div
                    key={item.id}
                    className={`card__product--small
                    ${
                      selectedGoods?.find((i) => i.id === item.id)
                        ? "active"
                        : ""
                    }
                    ${
                      item.is_discount
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    }
                  `}
                    onClick={() =>
                      !item.is_discount
                        ? handleSelectProduct(item)
                        : toast.error("Barang ini sudah memiliki promo!")
                    }
                  >
                    <div className="flex items-center gap-3">
                      {item.image_thumbnail ? (
                        <img
                          src={`${process.env.REACT_APP_PUBLIC_URL}/${item.image_thumbnail}`}
                          alt={item.name}
                          className="h-12 w-12 rounded-lg"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 bg-opacity-25">
                          <FaBoxes className="h-6 w-6 text-brand-500" />
                        </div>
                      )}
                      <div className="flex flex-col">
                        <p className="text-base font-semibold text-gray-800">
                          {item.name}
                        </p>
                        <p className="text-sm font-medium text-gray-700">
                          {item.category?.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-semibold text-brand-500">
                        {numericFormatter(
                          item.price?.original_price?.toString(),
                          {
                            decimalSeparator: ",",
                            thousandSeparator: ".",
                            prefix: "Rp",
                          }
                        )}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>
      </section>

      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="mt-8">
          <p className="mb-3 ml-3 mt-7 font-medium text-gray-600">
            Detail Promo
          </p>
          <div className="w-fit px-3">
            <SwitchField
              label="Aktivasi Promo"
              desc=""
              id="is_active"
              mb="mb-3"
              mt=""
              placeholder="Aktivasi Promo"
              register={register}
              errors={errors}
              validationSchema={{}}
            />
          </div>
          <div className="flex w-full gap-3">
            <Controller
              name="type"
              control={control}
              rules={{
                required: "Bentuk diskon harus diisi!",
              }}
              render={({ field }) => (
                <SelectField
                  field={field}
                  errors={errors}
                  label="Bentuk Diskon"
                  id="type"
                  placeholder="Pilih Bentuk Diskon"
                  extra="mb-3 flex-1 w-full"
                  options={discountTypes}
                  optionLabel="label"
                  optionValue="value"
                  rules={{
                    required: "Bentuk Diskon harus dipilih!",
                  }}
                  variant="outline"
                />
              )}
            />
            <Controller
              name="discount"
              control={control}
              rules={{
                required: "Diskon harus diisi!",
              }}
              render={({ field }) => (
                <NumFormatField
                  field={field}
                  extra="mb-3 flex-1 w-full"
                  decimalSeparator=","
                  thousandSeparator={
                    watchDiscountType?.value === DiscountType.NOMINAL ? "." : ""
                  }
                  errors={errors}
                  rules={{
                    required: "Diskon harus diisi!",
                  }}
                  label="Diskon"
                  id="discount"
                  placeholder="Masukkan diskon"
                  variant="outline"
                  prefix={
                    watchDiscountType?.value === DiscountType.NOMINAL
                      ? "Rp"
                      : ""
                  }
                  suffix={
                    watchDiscountType?.value === DiscountType.PERCENTAGE
                      ? "%"
                      : ""
                  }
                />
              )}
            />
          </div>
          <div className="flex w-full gap-3">
            <div className="flex-1 flex w-full flex-col gap-3">
              <InputField
                extra="mb-3 flex-1 w-full"
                label="Tanggal dan Waktu Mulai"
                placeholder="Masukkan tanggal mulai"
                id="start_at"
                type="date"
                variant="outline"
                register={register}
                errors={errors}
                validationSchema={{
                  required: "Tanggal mulai harus diisi!",
                }}
              />
              <Controller
                name="timestart_at"
                control={control}
                defaultValue={defaultTime}
                render={({ field }) => (
                  <TimePicker
                    disableClock={true}
                    format="HH:mm"
                    onChange={field.onChange}
                    value={field.value}
                    required
                  />
                )}
              />
            </div>
            <div className="flex-1 flex w-full flex-col gap-3">
              <InputField
                extra="mb-3 flex-1 w-full"
                label="Tanggal dan Waktu Berakhir"
                placeholder="Masukkan tanggal berakhir"
                id="end_at"
                type="date"
                variant="outline"
                register={register}
                errors={errors}
                validationSchema={{
                  required: "Tanggal berakhir harus diisi!",
                }}
              />
              <Controller
                name="timeend_at"
                control={control}
                defaultValue={defaultTime}
                render={({ field }) => (
                  <TimePicker
                    disableClock={true}
                    format="HH:mm"
                    onChange={field.onChange}
                    value={field.value}
                    required
                  />
                )}
              />
            </div>
          </div>
        </div>
        <Button
          children="Simpan"
          size="fit"
          variant="primary"
          extra="mt-7 mb-5"
          isLoading={isLoading}
        />
      </form>
    </Card>
  );
};

export default DiscountForm;
